import { v4 as uuidv4 } from "uuid";

import type { TImageManifestEntry, TSectionPage } from "models";
import type { JSONNode } from "./schema";
import { walkContentNodes } from "./content-walker";

/** Node-type/attr names below are plain strings (matching `schema.ts`'s convention) rather than
 *  importing `ImageBlockTypes` from `pages/tip-tap-editor` — `utils/templates` must not depend on
 *  that feature folder. */
const IMAGE_BLOCK_NODE_TYPE = "imageBlock";
const GENERIC_IMAGE_TYPE = "image";

function isGenericImageNode(node: JSONNode): boolean {
  return node.type === IMAGE_BLOCK_NODE_TYPE && node.attrs?.type === GENERIC_IMAGE_TYPE;
}

/** How many `imageBlock` nodes across all `pages` reference each manifest id. A page/section
 *  duplication deep-clones content, so the same id can legitimately show up more than once. */
export function countImageIdReferences(pages: TSectionPage[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const page of pages) {
    walkContentNodes(page.content, (node) => {
      if (!isGenericImageNode(node)) return;
      const imageId = node.attrs?.imageId;
      if (typeof imageId !== "string" || !imageId) return;
      counts.set(imageId, (counts.get(imageId) ?? 0) + 1);
    });
  }
  return counts;
}

/** Manifest entries no longer referenced by any node in `pages`. */
export function findOrphanedManifestEntries(
  pages: TSectionPage[],
  manifest: TImageManifestEntry[]
): TImageManifestEntry[] {
  const counts = countImageIdReferences(pages);
  return manifest.filter((entry) => !counts.get(entry.id));
}

/**
 * Migration for legacy (pre-`schema`) documents: walks `pages`, finds every generic `imageBlock`
 * node carrying a legacy `documentId` + `src`, and rewrites it to carry a manifest `imageId`
 * instead. Nodes that already shared the same `documentId` (a pre-manifest duplicate) collapse to
 * a single manifest entry, consistent with the new reference-counting model. Nodes with no
 * `documentId` (broken/orphaned legacy refs — expected to be rare) are left untouched rather than
 * guessed at. Returns new page objects; does not mutate `pages` in place.
 */
export function migrateLegacyImageReferences(pages: TSectionPage[]): {
  pages: TSectionPage[];
  manifest: TImageManifestEntry[];
} {
  const idByLegacyDocId = new Map<number, string>();
  const manifest: TImageManifestEntry[] = [];

  const migratedPages = pages.map((page) => {
    if (!page.content) return page;
    const content = JSON.parse(JSON.stringify(page.content));

    walkContentNodes(content, (node) => {
      if (!isGenericImageNode(node) || !node.attrs) return;

      const legacyDocId = node.attrs.documentId;
      const legacySrc = typeof node.attrs.src === "string" ? node.attrs.src : "";
      delete node.attrs.documentId;
      node.attrs.src = "";

      if (typeof legacyDocId !== "number") return;

      let imageId = idByLegacyDocId.get(legacyDocId);
      if (!imageId) {
        imageId = uuidv4();
        idByLegacyDocId.set(legacyDocId, imageId);
        manifest.push({
          id: imageId,
          docId: legacyDocId,
          filename: legacySrc.split("/").pop() || "",
          url: legacySrc,
        });
      }
      node.attrs.imageId = imageId;
    });

    return { ...page, content };
  });

  return { pages: migratedPages, manifest };
}

/** The single place a manifest entry becomes a displayable `<img src>`. */
export function resolveImageSrc(entry: TImageManifestEntry | undefined, storageToken: string): string {
  if (!entry?.url) return "";
  return `${entry.url}?${storageToken}`;
}

/**
 * Resolves every generic `imageBlock`'s `imageId` back to a display `src`, for the browser-side
 * pdfmake pipeline (`@defectwise/pdf-report-core`'s `getImageBlock` reads `attrs.src` directly and
 * has no knowledge of the manifest). Returns a new content tree — never mutates the editor's own
 * `imageId`-only state, which must stay free of raw blob URLs.
 */
export function resolveManifestImageSrcs(content: unknown, manifest: TImageManifestEntry[], storageToken: string): unknown {
  if (!content) return content;
  const manifestById = new Map(manifest.map((entry) => [entry.id, entry]));
  const cloned = JSON.parse(JSON.stringify(content));

  walkContentNodes(cloned, (node) => {
    if (!isGenericImageNode(node) || !node.attrs) return;
    const imageId = node.attrs.imageId;
    node.attrs.src = typeof imageId === "string" ? resolveImageSrc(manifestById.get(imageId), storageToken) : "";
  });

  return cloned;
}
